import {
  assignSelectionOrdersToPrefilledTravellers,
  ensureTravellerIds,
  getNextNegativeTravellerId,
  getNextSelectionOrder,
} from "@tm/insurance/utils";
import { GENDER_VALUES } from "@tm/isomorphic-utils";
import React, {
  useCallback,
  useEffect,
  useMemo,
  useRef,
  useState,
} from "react";
import { useFormContext, useWatch } from "react-hook-form";
import TravellerCard from "../../../AddTravellerDetail/TravellerDetailsV2";
import type {
  IFormFieldType,
  IGender,
  ITravellerDetailsFormV2,
} from "../../../AddTravellerDetail/type";
import {
  AddGlyph,
  DownloadGlyph,
  ExcelHeaderIcon,
  TickIcon,
  UploadGlyph,
} from "../../../CommonLandingMweb/shared/MwebIcons";
import type { TravellerData } from "../../../ExcelUpload/ExcelUpload.types";
import FormattedText, {
  getPlainText,
  type TextNodeType,
} from "../../../FormattedText/FormattedText";
import { useTravellerDetailsContext } from "../../contexts/TravellerDetailsV2Context";
import type { TravellerV2PageData } from "../../TravellerDetailsV2.types";
import { ExcelResultBottomSheet } from "./ExcelResultBottomSheet";
import {
  ActionRow,
  ActionRowLabel,
  CountBadge,
  DownloadIconBtn,
  ErrorBar,
  ExcelDesc,
  ExcelHeaderCol,
  ExcelHeaderRow,
  ExcelIconWrap,
  ExcelTitle,
  ExcelUploadSection,
  FileBar,
  FileBarLeft,
  FileName,
  HeaderRow,
  ManualCta,
  OrLabel,
  ReUploadBtn,
  SectionDivider,
  SubTitle,
  Title,
  TravellerListHeader,
  TravellerListTitle,
  TravellerListWrap,
  UploadIconBtn,
  ViewAllBtn,
} from "./Styles";
import { useExcelUpload } from "./useExcelUpload";

type SecondaryContactFields = {
  name?: IFormFieldType;
  dob?: IFormFieldType;
  gender?: IFormFieldType;
  passport?: IFormFieldType;
  nomineeName?: IFormFieldType;
};

interface ExcelUploadConfig {
  buttonText?: unknown;
  maxFileSizeMB?: number;
  maxRows?: number;
  isExcelBypass?: boolean;
  requiredFields?: (keyof TravellerData)[];
}

interface ExcelDownloadConfig {
  btnText?: unknown;
}

interface ExcelReUploadConfig {
  btnText?: unknown;
}

function RichOrString({
  value,
  as: Comp,
}: {
  value?: string | TextNodeType;
  as: React.ComponentType<{ children?: React.ReactNode }>;
}) {
  if (value == null) return null;
  if (typeof value === "string") return <Comp>{value}</Comp>;
  return (
    <Comp>
      <FormattedText value={value} />
    </Comp>
  );
}

export interface TravellerDetailSectionProps {
  title?: string;
  subTitle?: string;
  addTravellerCTA?: unknown;
  savedTravellers?: {
    name: string;
    gender?: string;
    dob?: number | string;
    passport?: string;
    nomineeName?: string;
    travellerId?: number | string;
    infoSource?: string;
  }[];
  secondaryContact?: {
    title?: string;
    isEditable?: boolean;
    fields?: SecondaryContactFields;
  };
  excelSectionData?: {
    title?: string | TextNodeType;
    description?: string | TextNodeType;
    bypassInfo?: string;
    info?: Record<string, any>;
    errors?: Record<string, string>;
    upload_error?: {
      action?: string;
      bottomsheetDetails?: {
        icon?: string;
        title?: string;
        subtitle?: string;
        cta?: Record<string, any>;
      };
    };
    download?: ExcelDownloadConfig;
    reUpload?: ExcelReUploadConfig;
    upload?: ExcelUploadConfig;
  };
  /** API may send these as sibling props rather than nested in excelSectionData */
  upload?: ExcelUploadConfig;
  download?: ExcelDownloadConfig;
  reUpload?: ExcelReUploadConfig;
  isPassportMandatory?: boolean;
  pageData?: TravellerV2PageData;
  onDownloadTemplate?: () => void;
  onUploadFile?: (file: File) => void;
}

export const TravellerDetailSection = ({
  title,
  subTitle,
  addTravellerCTA,
  savedTravellers,
  secondaryContact,
  excelSectionData: rawExcelSectionData,
  upload: topLevelUpload,
  download: topLevelDownload,
  reUpload: topLevelReUpload,
  isPassportMandatory,
  pageData: propPageData,
  onDownloadTemplate: propOnDownload,
  onUploadFile: propOnUpload,
}: TravellerDetailSectionProps) => {
  const ctx = useTravellerDetailsContext();
  const {
    travellerFieldArray,
    expandedTravellerIndex = -1,
    setExpandedTravellerIndex,
    forceShowAllTravellers = false,
    setForceShowAllTravellers,
    setUploadedExcelFile,
    secondaryContactFieldsRef,
    isLoading: ctxLoading,
  } = ctx;
  const pageData = propPageData ?? ctx.pageData;
  const onDownloadTemplate = propOnDownload ?? ctx.onDownloadTemplate;
  const onUploadFile = propOnUpload ?? ctx.onUploadTravellerFile;

  const excelSectionData = useMemo(() => {
    if (!rawExcelSectionData) return rawExcelSectionData;
    const merged = { ...rawExcelSectionData };
    if (topLevelUpload && !merged.upload) merged.upload = topLevelUpload;
    if (topLevelDownload && !merged.download)
      merged.download = topLevelDownload;
    if (topLevelReUpload && !merged.reUpload)
      merged.reUpload = topLevelReUpload;
    return merged;
  }, [rawExcelSectionData, topLevelUpload, topLevelDownload, topLevelReUpload]);

  const { control, setValue, getValues } =
    useFormContext<ITravellerDetailsFormV2>();
  if (!travellerFieldArray) {
    throw new Error(
      "TravellerDetailSection requires travellerFieldArray from TravellerDetailsV2 form context"
    );
  }
  const { fields: travellers, prepend } = travellerFieldArray;

  const travellerCount = pageData?.travellerCount ?? 0;
  const visibleCount = pageData?.visibleTravellersCount ?? 3;

  const {
    isExcelBypass,
    isUploading,
    isServerUploading,
    uploadedFileName,
    errorMessage,
    bsConfig,
    fileRef,
    reUploadRef,
    handleFileChange,
    handleBsAction,
    handleBsClose,
    handleDownload,
    handleReUpload,
  } = useExcelUpload({
    excelSectionData,
    travellerCount,
    pageData: pageData as any,
    travellerFieldArray,
    setExpandedTravellerIndex,
    setForceShowAllTravellers,
    setUploadedExcelFile,
    onUploadFile,
    onDownloadTemplate,
  });

  useEffect(() => {
    if (!secondaryContactFieldsRef) return;
    const f = secondaryContact?.fields;
    if (!f) {
      secondaryContactFieldsRef.current = {};
      return;
    }
    secondaryContactFieldsRef.current = {
      ...(f.name != null && {
        name: { isRequired: f.name.isRequired !== false },
      }),
      ...(f.dob != null && { dob: { isRequired: f.dob.isRequired !== false } }),
      ...(f.gender != null && {
        gender: { isRequired: f.gender.isRequired !== false },
      }),
      ...(f.passport != null && {
        passport: { isRequired: f.passport.isRequired !== false },
      }),
      ...(f.nomineeName != null && {
        nomineeName: { isRequired: f.nomineeName.isRequired !== false },
      }),
    };
  }, [secondaryContact, secondaryContactFieldsRef]);

  const hasSavedTravellers = Boolean(savedTravellers?.length);

  const savedTravellersInitRef = useRef(false);
  useEffect(() => {
    if (savedTravellersInitRef.current || !savedTravellers?.length) return;
    savedTravellersInitRef.current = true;

    const formTravellers = savedTravellers.map((t) => ({
      name: t.name || "",
      dob: t.dob != null ? String(t.dob) : "",
      gender: t.gender
        ? t.gender.charAt(0).toUpperCase() + t.gender.slice(1).toLowerCase()
        : "",
      passport: t.passport || "",
      nomineeName: t.nomineeName || "",
      isSelected: false,
      isEdited: false,
      isSavedTraveller: true,
      travellerId: t.travellerId,
      infoSource: t.infoSource,
    }));

    const { travellers: withSelectionOrder } =
      assignSelectionOrdersToPrefilledTravellers(formTravellers);
    travellerFieldArray.replace(
      ensureTravellerIds(
        withSelectionOrder.map((traveller, index) => ({
          ...traveller,
          travellerId:
            formTravellers[index]?.travellerId ?? traveller.travellerId,
          infoSource:
            formTravellers[index]?.infoSource ?? traveller.infoSource,
        }))
      )
    );
    setExpandedTravellerIndex?.(-1);
  }, [savedTravellers, travellerFieldArray, setExpandedTravellerIndex]);

  const travellerData = useWatch({ control, name: "travellerData" });
  const selectedCount = useMemo(
    () => travellerData?.filter((t) => t.isSelected)?.length ?? 0,
    [travellerData]
  );

  const countLabel =
    travellerCount > 0
      ? `${selectedCount}/${travellerCount} Added`
      : `${selectedCount} Added`;

  const [showAll, setShowAll] = useState(false);
  const travellerRef = useRef<HTMLDivElement>(null);

  const fields = secondaryContact?.fields;
  const genderTypes = fields?.gender?.values ?? GENDER_VALUES;
  const hasExcel = Boolean(excelSectionData);
  const hasTravellers = travellers.length > 0;
  const showFileBar = hasTravellers && Boolean(uploadedFileName);
  const showExcelFullCard = hasExcel && !showFileBar;

  const listExpanded = forceShowAllTravellers || showAll;
  const displayedTravellers = listExpanded
    ? travellers
    : travellers.slice(0, visibleCount);

  const handleAddTraveller = useCallback(() => {
    if (!travellerCount || selectedCount < travellerCount) {
      const currentTravellers = getValues("travellerData") || [];
      prepend({
        name: "",
        dob: "",
        gender: "",
        passport: "",
        nomineeName: "",
        isSelected: true,
        isEdited: true,
        isSavedTraveller: false,
        selectionOrder: getNextSelectionOrder(currentTravellers),
        travellerId: getNextNegativeTravellerId(currentTravellers),
      });
      setExpandedTravellerIndex?.(0);
    }
  }, [
    travellerCount,
    selectedCount,
    prepend,
    setExpandedTravellerIndex,
    getValues,
  ]);

  const handleGenderChange = useCallback(
    (item: IGender, index: number) => {
      setValue(`travellerData.${index}.gender`, item.label);
    },
    [setValue]
  );
  const handleArrowButton = useCallback(
    (index: number) => {
      setExpandedTravellerIndex?.((prev) => (prev === index ? -1 : index));
    },
    [setExpandedTravellerIndex]
  );
  const handleTravellerSelect = useCallback(
    (_value: boolean, index: number) => {
      const cur = getValues(`travellerData.${index}.isSelected`);
      if (cur) {
        setExpandedTravellerIndex?.(-1);
        setValue(`travellerData.${index}.isSelected`, false);
      } else if (!travellerCount || selectedCount < travellerCount) {
        setExpandedTravellerIndex?.(index);
        setValue(`travellerData.${index}.isSelected`, true);
        setValue(
          `travellerData.${index}.selectionOrder`,
          getNextSelectionOrder(getValues("travellerData") || [])
        );
      }
    },
    [
      getValues,
      setValue,
      travellerCount,
      selectedCount,
      setExpandedTravellerIndex,
    ]
  );
  const saveTravellerBlur = useCallback(
    (index: number = -1) => {
      if (index !== -1) setValue(`travellerData.${index}.isEdited`, true);
    },
    [setValue]
  );

  const defaultTravellerAddedRef = useRef(false);
  useEffect(() => {
    if (defaultTravellerAddedRef.current) return;
    if (!hasExcel && !hasSavedTravellers && travellers.length === 0) {
      defaultTravellerAddedRef.current = true;
      const currentTravellers = getValues("travellerData") || [];
      prepend({
        name: "",
        dob: "",
        gender: "",
        passport: "",
        nomineeName: "",
        isSelected: true,
        isEdited: true,
        isSavedTraveller: false,
        selectionOrder: 0,
        travellerId: getNextNegativeTravellerId(currentTravellers),
      });
      setExpandedTravellerIndex?.(0);
    }
  }, [
    hasExcel,
    hasSavedTravellers,
    travellers.length,
    prepend,
    setExpandedTravellerIndex,
    getValues,
  ]);

  const downloadConfig = excelSectionData?.download;
  const uploadConfig = excelSectionData?.upload;
  const reUploadConfig = excelSectionData?.reUpload;

  const renderExcelScaffold = (opts?: { showOrDivider?: boolean }) => {
    const showOrDivider = opts?.showOrDivider ?? !hasTravellers;
    return (
      <>
        <SectionDivider style={{ margin: "16px 0" }} />
        <ExcelUploadSection>
          <ExcelHeaderRow>
            <ExcelIconWrap>
              <ExcelHeaderIcon />
            </ExcelIconWrap>
            <ExcelHeaderCol>
              <RichOrString
                value={
                  excelSectionData!.title ?? "Add Travellers via Excel Upload"
                }
                as={ExcelTitle}
              />
              {excelSectionData!.description != null ? (
                <RichOrString
                  value={excelSectionData!.description}
                  as={ExcelDesc}
                />
              ) : (
                <ExcelDesc>
                  File format: .xls, .xlsx | size up-to 10MB
                </ExcelDesc>
              )}
            </ExcelHeaderCol>
          </ExcelHeaderRow>
          {downloadConfig?.btnText ? (
            <ActionRow>
              <ActionRowLabel>
                <FormattedText value={downloadConfig.btnText as any} />
              </ActionRowLabel>
              <DownloadIconBtn
                type="button"
                onClick={handleDownload}
                aria-label={
                  getPlainText(downloadConfig.btnText as TextNodeType) ||
                  "Download"
                }
              >
                <DownloadGlyph />
              </DownloadIconBtn>
            </ActionRow>
          ) : null}
          {uploadConfig?.buttonText ? (
            <ActionRow style={{ marginBottom: 0 }}>
              <ActionRowLabel>
                <FormattedText value={uploadConfig.buttonText as any} />
              </ActionRowLabel>
              <UploadIconBtn
                type="button"
                onClick={() => fileRef.current?.click()}
                disabled={isUploading || isServerUploading}
                aria-label={
                  getPlainText(uploadConfig.buttonText as TextNodeType) ||
                  "Upload"
                }
              >
                <UploadGlyph />
              </UploadIconBtn>
            </ActionRow>
          ) : null}
          {errorMessage ? <ErrorBar>{errorMessage}</ErrorBar> : null}
        </ExcelUploadSection>
        {!isExcelBypass && showOrDivider ? (
          <SectionDivider style={{ margin: "16px 0" }}>
            <OrLabel>OR</OrLabel>
          </SectionDivider>
        ) : null}
      </>
    );
  };

  return (
    <>
      <div
        style={{
          background: "#fff",
          borderRadius: 16,
          padding: 16,
          overflow: "hidden",
        }}
      >
        <HeaderRow>
          <Title>{title || "Add Travellers"}</Title>
          {!isExcelBypass && <CountBadge>{countLabel}</CountBadge>}
        </HeaderRow>
        {subTitle ? <SubTitle>{subTitle}</SubTitle> : null}
        {isExcelBypass && excelSectionData?.bypassInfo ? (
          <SubTitle>
            <FormattedText value={excelSectionData.bypassInfo} />
          </SubTitle>
        ) : null}

        <input
          ref={fileRef}
          type="file"
          accept=".xls,.xlsx"
          hidden
          onChange={handleFileChange}
        />
        <input
          ref={reUploadRef}
          type="file"
          accept=".xls,.xlsx"
          hidden
          onChange={handleFileChange}
        />

        {showExcelFullCard
          ? renderExcelScaffold({ showOrDivider: !hasTravellers })
          : null}

        {hasTravellers ? (
          <>
            <SectionDivider style={{ margin: "16px 0" }} />

            {showFileBar ? (
              <>
                <FileBar>
                  <FileBarLeft>
                    <TickIcon />
                    <FileName>{uploadedFileName}</FileName>
                  </FileBarLeft>
                  {reUploadConfig?.btnText ? (
                    <ReUploadBtn type="button" onClick={handleReUpload}>
                      <FormattedText value={reUploadConfig.btnText as any} />
                    </ReUploadBtn>
                  ) : null}
                </FileBar>
                {errorMessage ? <ErrorBar>{errorMessage}</ErrorBar> : null}
                <SectionDivider style={{ margin: "16px 0" }} />
              </>
            ) : null}

            <TravellerListHeader>
              <TravellerListTitle>
                {hasSavedTravellers ? "Saved Travellers" : "Travellers"}
              </TravellerListTitle>
              {travellers.length > visibleCount ? (
                <ViewAllBtn type="button" onClick={() => setShowAll((p) => !p)}>
                  {showAll ? "View Less" : "View All"}
                </ViewAllBtn>
              ) : null}
            </TravellerListHeader>

            <TravellerListWrap>
              {displayedTravellers.map((traveller, index) => (
                <div key={traveller.id} data-traveller-index={index}>
                  <TravellerCard
                    type=""
                    travellerIndex={index}
                    travellerRef={travellerRef}
                    isLoading={Boolean(ctxLoading)}
                    firstErrorIndex={expandedTravellerIndex}
                    travellerNumber={`Traveller ${index + 1}`}
                    genderTypes={genderTypes as IGender[]}
                    travellerSelected={expandedTravellerIndex === index}
                    selectedGender={traveller.gender}
                    isSelected={traveller.isSelected}
                    count={selectedCount}
                    travellerCount={travellerCount}
                    handleGenderChange={(item) =>
                      handleGenderChange(item, index)
                    }
                    handleArrowButton={() => handleArrowButton(index)}
                    handleTravellerSelect={handleTravellerSelect}
                    saveFormValuesOnBlur={saveTravellerBlur}
                    secondaryContactData={fields}
                    isSavedTravellers={hasSavedTravellers}
                    isEditable={secondaryContact?.isEditable !== false}
                    isCompactView
                    hideHeader
                  />
                </div>
              ))}
            </TravellerListWrap>

            <SectionDivider style={{ margin: "12px 0" }} />
          </>
        ) : null}

        {!isExcelBypass && (
          <ManualCta
            type="button"
            onClick={handleAddTraveller}
            disabled={travellerCount > 0 && selectedCount === travellerCount}
          >
            <AddGlyph />
            {addTravellerCTA ? (
              <FormattedText value={addTravellerCTA as any} />
            ) : (
              "ADD TRAVELLERS MANUALLY"
            )}
          </ManualCta>
        )}
      </div>

      {bsConfig ? (
        <ExcelResultBottomSheet
          config={bsConfig}
          onAction={handleBsAction}
          onClose={handleBsClose}
        />
      ) : null}
    </>
  );
};
